/**
 * Jetons de couleur exposés au JavaScript.
 *
 * Recharts prend ses couleurs en props, pas en CSS : ces constantes doublent
 * donc les variables de `index.css`. Elles sont la seule duplication assumée du
 * projet — tenir les deux listes synchronisées.
 *
 * ── Comment cette palette a été obtenue ─────────────────────────────────────
 * Les emplacements de série sont validés, pas choisis à l'œil, pour la surface
 * sombre #252A34 :
 *   · bande de clarté OKLCH 0,48–0,67   · chroma ≥ 0,10
 *   · ΔE CVD (protanopie / deutéranopie) 11,2 — cible ≥ 8
 *   · ΔE vision normale 35,1 — plancher ≥ 15
 *   · contraste ≥ 3:1 sur la surface
 *
 * `SERIES_2` n'est PAS le cyan de marque #08D9D6 : sa clarté OKLCH vaut 0,801,
 * au-dessus de la bande sombre, ce qui le rend trop clair en aplat. C'est le
 * même ton descendu d'un cran. Le cyan de marque reste dans l'interface (liens,
 * accents, anneau de focus), où il donne 8,2:1.
 *
 * ── Règle d'attribution ─────────────────────────────────────────────────────
 * Les emplacements s'attribuent dans l'ordre et ne se cyclent jamais. La
 * couleur suit l'entité, jamais son rang : un filtre qui change le nombre de
 * séries ne doit pas repeindre les survivantes.
 */

export const COLORS = {
  surface: '#252A34',
  surfaceRaised: '#2C323E',
  border: '#333A47',
  grid: '#39404E',

  textPrimary: '#F2F4F8',
  textSecondary: '#A7B0C0',
  textMuted: '#8D97A8',

  brandRose: '#FF2E63',
  brandCyan: '#08D9D6',

  /** Emplacement 1. Toute série unique le prend. */
  series1: '#FF2E63',
  /** Emplacement 2. N'apparaît qu'à partir de deux séries. */
  series2: '#05A9A7',
} as const;

/**
 * Palette CATÉGORIELLE — parts d'un tout (camemberts de répartition).
 *
 * Dix emplacements, dans un ORDRE trouvé par recherche et validé avec le
 * script du skill dataviz sur la surface #252A34 (mode sombre), « Autres » en
 * onzième part et la dernière part contre la première comprises :
 *   · bande de clarté 0,48–0,67, chroma ≥ 0,10, contraste ≥ 3:1 — PASS ;
 *   · parts VOISINES : ΔE CVD ≥ 9,4 (cible 8), ΔE vision normale ≥ 18,9
 *     (plancher 15) — PASS.
 *
 * ⚠️ Dix teintes ne se distinguent pas toutes deux à deux (seules les
 * voisines sont garanties) : un anneau à dix parts n'est lisible qu'avec sa
 * légende chiffrée et son tableau, qui sont toujours affichés. Ne jamais
 * réordonner sans relancer la validation.
 *
 * Attribution : à l'ENTITÉ quand l'ensemble est fixe (les canaux : Shopify
 * reste rose). Pour les pays, dont le top 10 change à chaque période, par
 * RANG dans cet ordre — c'est ce qui garantit des parts voisines distinctes —
 * et le nom de chaque pays est toujours écrit dans la légende et le tableau.
 */
export const CATEGORICAL_COLORS = [
  '#FF2E63', '#1FA3C8', '#2E9E3E', '#9085E9', '#C98500',
  '#D55181', '#3987E5', '#199E70', '#D95926', '#B86BD6',
] as const;

/**
 * « Autres » : un neutre, hors bande de clarté À DESSEIN — ce n'est pas une
 * catégorie. Clair plutôt que foncé : les gris foncés se confondaient avec le
 * rose en protanopie ; celui-ci passe contre ses deux voisins de l'anneau (la
 * dixième couleur et le rose).
 */
export const OTHER_COLOR = '#B8BFCC';

/**
 * Rampe séquentielle — une seule teinte, six pas de clarté croissante.
 *
 * Sert à la heatmap d'activité et aux histogrammes ORDINAUX (durée de vie,
 * jours d'activité) : quand l'ordre des catégories porte du sens, la couleur
 * doit le montrer. Six pas exactement, et les seaux sont dimensionnés pour
 * correspondre.
 *
 * Séquentielle et non catégorielle : une heatmap encode une MAGNITUDE. Un
 * arc-en-ciel y ferait croire à des catégories et rendrait l'ordre illisible.
 *
 * Construite sur la teinte du rose de marque (13,8° en OKLCH) et validée pour
 * le mode sombre sur la surface #252A34 :
 *   · clarté strictement croissante   · écart de clarté ≥ 0,06 entre pas
 *   · pas le plus sombre à 2,07:1 sur la surface (plancher 2:1)
 *   · étalement de teinte : 1°
 *
 * Le chroma est volontairement plus bas que celui du rose de marque (0,12 contre
 * 0,238) : à pleine saturation, les six pas ne se distinguent plus par leur
 * clarté.
 */
export const SEQUENTIAL_RAMP = [
  '#953C49',
  '#A94E5A',
  '#BE616C',
  '#D4747F',
  '#E98792',
  '#FF9BA5',
] as const;

/** Case sans aucune action : ni un pas de la rampe, ni la surface nue. */
export const HEATMAP_EMPTY = '#2B3140';

/**
 * Couleurs de statut — réservées, jamais réutilisées comme couleur de série.
 *
 * ⚠️ Ce ne sont PAS les valeurs de la charte. Mesurées sur la surface #252A34,
 * le vert #0A8F4A tombe à 3,45:1 et le rouge #C0392B à 2,65:1 — le second est
 * insuffisant même pour une simple marque. Ces valeurs sont calibrées pour le
 * PDF, qui a du texte sombre sur fond blanc ; sur fond sombre il faut les
 * relever. Mêmes teintes, un cran plus clair :
 *   good     #2BBE72 → 5,97:1
 *   warning  #E0A02A → 6,33:1
 *   critical #E8706A → 4,77:1
 *
 * Et elles ne voyagent jamais seules : toujours accompagnées d'une icône et
 * d'un libellé, pour que l'information ne repose pas sur la couleur.
 */
export const STATUS_COLORS = {
  good: '#2BBE72',
  warning: '#E0A02A',
  critical: '#E8706A',
} as const;

/**
 * Voiles de statut, pour teinter une carte entière.
 *
 * 12 % d'opacité, exactement comme l'encart d'erreur de la page de connexion :
 * la carte se lit « rouge » au premier coup d'œil sans que la luminance du fond
 * bouge assez pour entamer le contraste du texte posé dessus (13,1:1 pour le
 * texte principal, il reste au-dessus de 12:1 sous le voile).
 *
 * Un aplat saturé aurait obligé à repeindre tout le texte de la carte — et le
 * grand nombre d'une tuile de KPI n'a aucune couleur de remplacement qui tienne
 * le contraste.
 *
 * Valeurs RGB reprises de `STATUS_COLORS` ci-dessus : les tenir synchronisées.
 */
export const STATUS_TINTS = {
  good: 'rgb(43 190 114 / 0.12)',
  warning: 'rgb(224 160 42 / 0.12)',
  critical: 'rgb(232 112 106 / 0.12)',
} as const;

/** Opacité des aplats : un voile, jamais un bloc saturé. */
export const AREA_FILL_OPACITY = 0.14;

/** Épaisseur des traits, arrondi des extrémités de barre — specs fixes. */
export const LINE_WIDTH = 2;
export const BAR_RADIUS = 4;
export const BAR_MAX_SIZE = 24;

/** Axes et grille, volontairement récessifs. */
export const AXIS_STYLE = {
  stroke: COLORS.grid,
  fontSize: 12,
  fill: COLORS.textMuted,
} as const;

/**
 * Couleurs du verdict d'un marqueur.
 *
 * Reprennent les couleurs de statut, qui sont déjà réservées et jamais
 * réutilisées comme couleur de série : un marqueur est une annotation, il ne
 * doit pas pouvoir se confondre avec une courbe.
 *
 * `neutral` n'est pas une couleur de statut mais du texte secondaire — un
 * verdict « stable » ou « pas assez de recul » ne mérite aucune alarme.
 */
export const IMPACT_TONE_COLORS = {
  good: STATUS_COLORS.good,
  bad: STATUS_COLORS.critical,
  neutral: COLORS.textSecondary,
} as const;

/**
 * Trait d'un marqueur : gris, pointillé, fin.
 *
 * Volontairement récessif et NON coloré selon le verdict : le trait dit
 * « il s'est passé quelque chose ce jour-là », c'est de l'annotation, pas de
 * la donnée. Le verdict est porté par le numéro en haut du trait et par la
 * bande sous le graphique, où il peut être écrit en mots.
 */
export const MARKER_LINE = {
  stroke: COLORS.textMuted,
  strokeDasharray: '4 4',
  strokeWidth: 1,
} as const;

/**
 * Couleur de chaque plateforme publicitaire, fixe d'un graphique à l'autre :
 * Meta reste bleu et Amazon Ads ambre, qu'on décoche l'autre ou non. Paire
 * validée sur la surface sombre (ΔE CVD 21).
 */
export const AD_PLATFORM_COLORS = {
  meta: CATEGORICAL_COLORS[1],
  amazon: CATEGORICAL_COLORS[4],
  google: CATEGORICAL_COLORS[2],
} as const;
